import { Ikon } from '../status/Ikon.tsx';

/**
 * Sporingslinjen (DD-56 presisert K3, DD-28): hvor saken står, som en vannrett linje med
 * ett ledd per steg og fristen som tall i det siste. Den ERSTATTER «Deretter: …» — aldri
 * begge. Komponenten eier bare visningen; leddene bygges der saken finnes (detaljen i
 * panelet nå, programsiden i R2 og portalen i R7 senere), så reglene står ett sted.
 *
 * Tilstandene, og hvordan de skilles UTEN farge (DD-08):
 *   ferdig    hake på fylt flate
 *   naa       statusens eget ikon i ring, og `aria-current="step"`
 *   kommende  stiplet ring uten ikon
 *   brutt     ⚠ i ring, og merknaden «N dager over» i ord
 */
export type LeddTilstand = 'ferdig' | 'naa' | 'kommende' | 'brutt';

export interface SporLedd {
  /** Nøkkel og testid-suffiks: `spor-<key>`. */
  key: string;
  navn: string;
  /** Datoen eller merknaden under navnet, i small — det er meta, ikke beslutningen. */
  under?: string;
  /** Datoen som tall (h1). Bare det siste leddet bærer den: det er sakens ene tidsløfte (DD-28). */
  tall?: string;
  /** «18 dager over» — står i ord ved siden av tallet når fristen er brutt. */
  merknad?: string;
  tilstand: LeddTilstand;
  /** Statusens ikon på nå-leddet. Ferdige ledd har hake og brutte ⚠ uansett. */
  ikon?: string;
  /**
   * Ekstra tone på leddet, for programsiden (R2.3): `varsel` er «Avsluttes» — rapporten
   * venter på leietakeren, og det er en advarsel, ikke en feil — og `avbrutt` er det
   * fylte røde leddet linjen stopper på. Innboksen bruker ingen av dem.
   */
  tone?: 'varsel' | 'avbrutt';
  testid?: string;
}

const NODEIKON: Partial<Record<LeddTilstand, string>> = {
  ferdig: 'checkmark-circle-outline',
  brutt: 'warning-outline',
};

export function Sporingslinje({ ledd, etikett }: { ledd: SporLedd[]; etikett: string }) {
  return (
    <ol className="panel-spor" aria-label={etikett} data-testid="sporingslinje">
      {ledd.map((l, i) => {
        const ikon = NODEIKON[l.tilstand] ?? l.ikon;
        return (
          <li
            key={l.key}
            className={`panel-spor-ledd panel-spor-${l.tilstand}${l.tall ? ' panel-spor-maal' : ''}${l.tone ? ` panel-spor-${l.tone}` : ''}`}
            aria-current={l.tilstand === 'naa' ? 'step' : undefined}
            data-testid={l.testid ?? `spor-${l.key}`}
          >
            <span className="panel-spor-rad">
              <span className="panel-spor-node">{ikon ? <Ikon navn={ikon} /> : null}</span>
              {i < ledd.length - 1 ? <span className="panel-spor-strek" aria-hidden="true" /> : null}
            </span>
            <span className="panel-spor-navn">{l.navn}</span>
            {l.tall ? <span className="panel-spor-tall">{l.tall}</span> : null}
            {l.under ? <span className="panel-spor-under">{l.under}</span> : null}
            {l.merknad ? (
              <span className="panel-spor-merknad">
                <Ikon navn="warning-outline" /> {l.merknad}
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
